import {
  Box,
  Button,
  Header,
  SpaceBetween,
  Container,
  Table,
  Pagination,
  TextFilter,
  Alert,
} from '@amzn/awsui-components-console';
import { useState, useEffect, useContext } from 'react';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { useAuthState } from '../../../authentication/context';
import { callApi } from '../../../client';
import {
  GetPermissionsOutput,
  UpdatePermissionsInput,
  GetPermissionsCommand,
  GetPermissionsCommandOutput,
  UpdatePermissionsCommand,
  UpdatePermissionsCommandOutput,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { PermissionsContext } from './permissionscontext';
import { NoTokenError } from '../../../errors/NoTokenError';
import { useApiErrors } from '../../../components/ApiErrorContext';

export const PermissionsView = ({
  id,
  workStatus,
}: {
  id: string;
  workStatus?: string;
}) => {
  const { isAuthenticated, token } = useAuthState();

  const {
    readPermissions,
    writePermissions,
    updateReadPermissions,
    updateWritePermissions,
    notifications,
    setNotifications,
  } = useContext(PermissionsContext);

  const permissionsId = 'updatePermissionsMsg';
  const { pushError } = useApiErrors();

  async function callGetReadPermissionsApi(id: string) {
    if (!token) throw new NoTokenError();
    try {
      const response: GetPermissionsCommandOutput = await callApi(
        new GetPermissionsCommand({
          launchId: id,
        }),
        token
      );
      updateReadPermissions(response.readAcl);
    } catch (err) {
      console.log('error with GET Permissions', err);
    }
  }

  async function callGetWritePermissionsApi(id: string) {
    if (!token) throw new NoTokenError();
    try {
      const response: GetPermissionsCommandOutput = await callApi(
        new GetPermissionsCommand({
          launchId: id,
        }),
        token
      );
      updateWritePermissions(response.writeAcl);
    } catch (err) {
      console.log('error with GET Permissions', err);
    }
  }

  async function callUpdatePermissionsApi(body: UpdatePermissionsInput) {
    if (!token) throw new NoTokenError();
    try {
      const response: UpdatePermissionsCommandOutput = await callApi(
        new UpdatePermissionsCommand({
          launchId: id,
          readAcl: body.readAcl,
          writeAcl: body.writeAcl,
        }),
        token
      );
      if (response.$metadata.httpStatusCode == 200) {
        updateReadPermissions(body.readAcl);
        updateWritePermissions(body.writeAcl);
        setSelectedItems([]);
        setNotifications((prev) => [
          ...prev.filter((item) => item.id !== permissionsId),
          {
            id: permissionsId,
            type: 'success',
            content: 'removing permissions succeed',
            dismissible: true,
            dismissLabel: 'Dismiss',
            onDismiss: () =>
              setNotifications((items) =>
                items.filter((item) => item.id !== permissionsId)
              ),
          },
        ]);
      }
    } catch (err: any) {
      pushError(
        'Remove Permissions Failed',
        err?.message || 'Failed to remove permissions'
      );
    }
  }

  interface Permissions {
    name: string;
    permission: ('read' | 'write')[];
  }

  const [allItems, setItems] = useState<Permissions[]>([]);

  useEffect(() => {
    const permissions: Permissions[] = [];
    readPermissions.forEach((name) => {
      const permission: ('read' | 'write')[] = [];
      if (writePermissions.includes(name)) {
        permission.push('read', 'write');
      } else {
        permission.push('read');
      }
      permissions.push({ name, permission });
    });

    writePermissions.forEach((name) => {
      if (!readPermissions.includes(name)) {
        permissions.push({ name, permission: ['write'] });
      }
    });

    setItems(permissions);
  }, [readPermissions, writePermissions]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await callGetReadPermissionsApi(id);
        await callGetWritePermissionsApi(id);
      } catch (error) {
        console.error('Error fetching permissions:', error);
      }
    };

    void fetchData();
  }, [id]);

  const [selectedItems, setSelectedItems] = useState<Permissions[]>([]);

  const { items, actions, collectionProps, filterProps, paginationProps } =
    useCollection(allItems, {
      filtering: {
        filteringFunction: (item: Permissions, filteringText: string) =>
          item.name.toLowerCase().includes(filteringText.toLowerCase()),
        noMatch: (
          <Box margin={{ vertical: 'xl' }} textAlign='center' color='inherit'>
            <SpaceBetween size='xxl'>
              <SpaceBetween size='xs'>
                <div>
                  <b>No matches</b>
                  <Box variant='p' color='inherit'>
                    Try search again
                  </Box>
                </div>
                <Button
                  onClick={() => {
                    actions.setFiltering('');
                  }}
                >
                  Clear Filter
                </Button>
              </SpaceBetween>
            </SpaceBetween>
          </Box>
        ),
      },
      pagination: { pageSize: 10 },
      sorting: {},
      selection: {},
    });

  return (
    <SpaceBetween direction='vertical' size='s'>
      {workStatus === 'GROUPED' && (
        <Alert type='info'>
          This task belongs to a group. Adding permissions to this task also
          grants those permissions to the group it belongs to, since group
          permissions are automatically kept in sync with member task
          permissions.
        </Alert>
      )}
      <Container>
        <SpaceBetween size='m' direction='vertical'>
          <Table
            {...collectionProps}
            resizableColumns
            variant='borderless'
            onSelectionChange={({ detail }) =>
              setSelectedItems(detail.selectedItems)
            }
            selectedItems={selectedItems}
            columnDefinitions={[
              {
                id: 'name',
                header: 'User alias or POSIX group',
                cell: (item: Permissions) => item.name,
                sortingField: 'name',
                isRowHeader: true,
              },
              {
                id: 'permissions',
                header: 'Permissions',
                cell: (item: Permissions) => item.permission.join(', '),
                sortingField: 'permissions',
                isRowHeader: true,
              },
            ]}
            items={items}
            loadingText='Loading resources'
            selectionType='multi'
            trackBy='name'
            header={
              <Header
                actions={
                  <SpaceBetween direction='horizontal' size='xs'>
                    {' '}
                    <Button
                      onClick={() => {
                        const filteredReadPermissions: string[] =
                          readPermissions.filter(
                            (permission) =>
                              !selectedItems.some(
                                (item) => item.name === permission
                              )
                          );

                        const body: UpdatePermissionsInput = {
                          launchId: id,
                          readAcl: filteredReadPermissions,
                          writeAcl: writePermissions,
                        };
                        void callUpdatePermissionsApi(body);
                      }}
                    >
                      Remove read permissions
                    </Button>
                    <Button
                      onClick={() => {
                        const filteredWritePermissions: string[] =
                          writePermissions.filter(
                            (permission) =>
                              !selectedItems.some(
                                (item) => item.name === permission
                              )
                          );
                        const body: UpdatePermissionsInput = {
                          launchId: id,
                          readAcl: readPermissions,
                          writeAcl: filteredWritePermissions,
                        };
                        void callUpdatePermissionsApi(body);
                      }}
                    >
                      Remove write permissions
                    </Button>
                    <Button
                      onClick={() => {
                        const filteredReadPermissions: string[] =
                          readPermissions.filter(
                            (permission) =>
                              !selectedItems.some(
                                (item) => item.name === permission
                              )
                          );
                        const filteredWritePermissions: string[] =
                          writePermissions.filter(
                            (permission) =>
                              !selectedItems.some(
                                (item) => item.name === permission
                              )
                          );
                        const body: UpdatePermissionsInput = {
                          launchId: id,
                          readAcl: filteredReadPermissions,
                          writeAcl: filteredWritePermissions,
                        };
                        void callUpdatePermissionsApi(body);
                      }}
                    >
                      Remove All
                    </Button>
                  </SpaceBetween>
                }
                counter={
                  selectedItems.length
                    ? '(' + selectedItems.length + '/' + allItems.length + ')'
                    : '(' + allItems.length + ')'
                }
              >
                Table with Permissions
              </Header>
            }
            pagination={<Pagination {...paginationProps} />}
            filter={
              <TextFilter
                filteringPlaceholder='Find alias or Posix group'
                {...filterProps}
              />
            }
          />
        </SpaceBetween>
      </Container>
    </SpaceBetween>
  );
};
